import React, { useState } from 'react';
import { X, Plus, User, Building, Trash2 } from 'lucide-react';
import { TasksBoard, BoardUser } from '../types/board';
import { AddUserToAccessModal } from './AddUserToAccessModal';
import { removeUserFromBoard, setDefaultBoard } from '../lib/boards';

interface BoardSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  board: TasksBoard | null;
  businessId: string;
  onBoardUpdated: () => void;
}

export const BoardSettingsModal: React.FC<BoardSettingsModalProps> = ({
  isOpen,
  onClose,
  board,
  businessId,
  onBoardUpdated
}) => {
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<BoardUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isUpdatingDefault, setIsUpdatingDefault] = useState(false);
  const [defaultError, setDefaultError] = useState<string | null>(null);

  const handleUserAdded = () => {
    onBoardUpdated(); // Refresh the board data
  };

  const handleDeleteUser = async (user: BoardUser) => {
    if (!board) return;
    
    // Prevent deleting the last user
    if (board.assigned_users.length <= 1) {
      setDeleteError('Cannot remove the last user from the board');
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await removeUserFromBoard(board.id, user);
      onBoardUpdated(); // Refresh the board data
      setUserToDelete(null);
    } catch (error) {
      console.error('Error removing user:', error);
      setDeleteError('Failed to remove user from board');
    } finally {
      setIsDeleting(false);
    }
  };

  const confirmDelete = (user: BoardUser) => {
    setUserToDelete(user);
    setDeleteError(null);
  };

  const cancelDelete = () => {
    setUserToDelete(null);
    setDeleteError(null);
  };

  const handleDefaultChange = async (isDefault: boolean) => {
    if (!board) return;

    setIsUpdatingDefault(true);
    setDefaultError(null);

    try {
      await setDefaultBoard(board.id, isDefault);
      onBoardUpdated(); // Refresh the board data
    } catch (error) {
      console.error('Error updating default board:', error);
      setDefaultError('Failed to update default board setting');
    } finally {
      setIsUpdatingDefault(false);
    }
  };

  if (!isOpen || !board) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Board Settings</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <div className="text-sm text-gray-600 mb-2">Board:</div>
            <div className="font-medium text-gray-900">{board.board_title}</div>
          </div>

          {/* Default Board Section */}
          <div className="mb-6">
            <div className="text-sm font-medium text-gray-900 mb-3">Board Settings</div>
            
            {/* Error Message for Default Setting */}
            {defaultError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">{defaultError}</div>
              </div>
            )}

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="default-board"
                checked={board.is_default}
                onChange={(e) => handleDefaultChange(e.target.checked)}
                disabled={isUpdatingDefault}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:opacity-50"
              />
              <label 
                htmlFor="default-board" 
                className={`text-sm ${
                  isUpdatingDefault ? 'text-gray-400' : 'text-gray-700'
                } cursor-pointer`}
              >
                Set as default board
              </label>
              {isUpdatingDefault && (
                <div className="text-xs text-gray-500">Updating...</div>
              )}
            </div>
            <div className="text-xs text-gray-500 mt-1 ml-7">
              The default board will open automatically when you visit the Tasks page.
            </div>
          </div>

          {/* Access Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-gray-900">Access</h3>
              <button 
                className="flex items-center px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md transition-colors"
                onClick={() => setIsAddUserModalOpen(true)}
              >
                <Plus size={16} className="mr-1" />
                Add User
              </button>
            </div>

            {/* Error Message */}
            {deleteError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                <div className="text-sm text-red-600">{deleteError}</div>
              </div>
            )}
            
            <div className="space-y-2">
              {board.assigned_users.map((user, index) => (
                <div key={index} className="flex items-center p-3 bg-gray-50 rounded-md">
                  <div className="flex items-center mr-3">
                    {user.type === 'business' ? (
                      <Building size={16} className="text-blue-500" />
                    ) : (
                      <User size={16} className="text-green-500" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900">{user.name}</div>
                    <div className="text-xs text-gray-500 capitalize">{user.type}</div>
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium mr-2 ${
                    user.type === 'business' 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {user.type === 'business' ? 'Admin' : 'Resource'}
                  </div>
                  <button
                    onClick={() => confirmDelete(user)}
                    disabled={board.assigned_users.length <= 1}
                    className={`p-1 rounded-md transition-colors ${
                      board.assigned_users.length <= 1
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                    }`}
                    title={board.assigned_users.length <= 1 ? 'Cannot remove the last user' : 'Remove user'}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            
            {board.assigned_users.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <div className="text-sm">No users assigned to this board</div>
              </div>
            )}
          </div>

          {/* Future settings sections can go here */}
          <div className="text-center py-8 text-gray-400">
            <div className="text-sm">Additional settings coming soon...</div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Remove User</h3>
              <p className="text-sm text-gray-600 mb-6">
                Are you sure you want to remove <strong>{userToDelete.name}</strong> from this board?
                This action cannot be undone.
              </p>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelDelete}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(userToDelete)}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
                >
                  {isDeleting ? 'Removing...' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add User to Access Modal */}
      <AddUserToAccessModal
        isOpen={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        board={board}
        businessId={businessId}
        onUserAdded={handleUserAdded}
      />
    </div>
  );
}; 